// Lógica pura del wizard "New Item": validaciones, pasos, borrador y el
// runner de tareas de Sage. Sin JSX, para que la pantalla solo pinte.
import { lookupInSage, sendToSage, COMPANIES, MAIN_COMPANY_ID } from '../../../services/newItemWizardService';
import { phoneDigits } from '../../Maintenance/Vendors';

// ─── Límites de Sage 100 (supuestos — confirmar contra el diccionario) ──────
export const LIMITS = {
    productLine: 4,
    productLineDesc: 25,
    vendorNo: 7,
    vendorName: 30,
    address: 30,
    city: 20,
    zip: 10,
    email: 250,
    itemCode: 30,
    itemDesc: 30,
    fullName: 100,
    um: 4,
};

// Unidades de medida del dropdown. Sale de los datos reales (2026-11-04): en
// itemCatalogue/itemCatalogue2 y en las líneas de facturas y órdenes del mirror,
// la única unidad que aparece es EACH (las columnas UM de CI_Item vienen vacías).
export const UNITS_OF_MEASURE = ['EACH'];
export const DEFAULT_UNIT_OF_MEASURE = 'EACH';

export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const blank = (v) => String(v === undefined || v === null ? '' : v).trim() === '';
const str = (v) => String(v === undefined || v === null ? '' : v).trim();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const STATE_RE = /^[A-Z]{2}$/;
const ITEM_CODE_RE = /^[A-Z0-9][A-Z0-9\-_./]*$/;
const UPC_RE = /^\d{8,14}$/;
const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const DIM_RE = /^\d+(\.\d{1,2})?$/;

const tooLong = (v, n) => str(v).length > n;

export function pick(obj, keys) {
    if (!obj) return '';
    for (let i = 0; i < keys.length; i++) {
        const v = obj[keys[i]];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
}
const same = (a, b) => str(a).toUpperCase() === str(b).toUpperCase();

// ─── Validaciones ────────────────────────────────────────────────────────────
export function validateProductLineForm(f) {
    const e = {};
    if (blank(f.code)) e.code = 'The system has not assigned a number yet';
    if (blank(f.desc)) e.desc = 'Required';
    else if (tooLong(f.desc, LIMITS.productLineDesc)) e.desc = `Max ${LIMITS.productLineDesc} characters`;
    return e;
}

export function validateVendorForm(f) {
    const e = {};
    if (blank(f.code)) e.code = 'The system has not assigned a number yet';
    if (blank(f.name)) e.name = 'Required';
    else if (tooLong(f.name, LIMITS.vendorName)) e.name = `Max ${LIMITS.vendorName} characters`;
    if (blank(f.address)) e.address = 'Required';
    else if (tooLong(f.address, LIMITS.address)) e.address = `Max ${LIMITS.address} characters`;
    if (blank(f.city)) e.city = 'Required';
    else if (tooLong(f.city, LIMITS.city)) e.city = `Max ${LIMITS.city} characters`;
    if (blank(f.state)) e.state = 'Required';
    else if (!STATE_RE.test(str(f.state))) e.state = '2-letter code (e.g. TX)';
    if (blank(f.zip)) e.zip = 'Required';
    else if (!ZIP_RE.test(str(f.zip))) e.zip = '12345 or 12345-6789';
    if (blank(f.phone)) e.phone = 'Required';
    else if (phoneDigits(f.phone).length !== 10) e.phone = 'Enter a valid 10-digit US number';
    if (blank(f.email)) e.email = 'Required';
    else if (!EMAIL_RE.test(str(f.email)) || tooLong(f.email, LIMITS.email)) e.email = 'Invalid email';
    return e;
}

export function validateProductForm(f) {
    const e = {};
    if (blank(f.itemCode)) e.itemCode = 'Required';
    else if (tooLong(f.itemCode, LIMITS.itemCode)) e.itemCode = `Max ${LIMITS.itemCode} characters`;
    else if (!ITEM_CODE_RE.test(str(f.itemCode))) e.itemCode = 'Letters, numbers and - _ . / only, no spaces';
    if (blank(f.description)) e.description = 'Required';
    else if (tooLong(f.description, LIMITS.itemDesc)) e.description = `Max ${LIMITS.itemDesc} characters (Sage limit)`;
    if (tooLong(f.fullName, LIMITS.fullName)) e.fullName = `Max ${LIMITS.fullName} characters`;
    if (blank(f.upc)) e.upc = 'Required';
    else if (!UPC_RE.test(str(f.upc))) e.upc = '8 to 14 digits, numbers only';
    if (!blank(f.upc2)) {
        if (!UPC_RE.test(str(f.upc2))) e.upc2 = '8 to 14 digits, numbers only';
        else if (str(f.upc2) === str(f.upc)) e.upc2 = 'Must be different from UPC';
    }
    if (blank(f.caseUpc)) e.caseUpc = 'Required';
    else if (!UPC_RE.test(str(f.caseUpc))) e.caseUpc = '8 to 14 digits, numbers only';
    if (blank(f.um)) e.um = 'Required';
    else if (!/^[A-Z]{1,4}$/.test(str(f.um))) e.um = 'Select a valid unit of measure';
    ['weight', 'length', 'height', 'width'].forEach((k) => {
        const v = str(f[k]);
        if (v === '') e[k] = 'Required';
        else if (!DIM_RE.test(v) || Number(v) <= 0 || Number(v) > 9999) e[k] = 'Greater than 0, max 2 decimals';
    });
    return e;
}

// ─── Sugerencia de ItemCode ─────────────────────────────────────────────────
// Agrupa los códigos del product line por prefijo + número final
// (FIX1002 → "FIX" + 1002) y sugiere el siguiente libre del grupo. Si lo
// que el usuario escribió empieza con un prefijo conocido, sugiere ese grupo;
// si no, el grupo con más items.
const CODE_PARTS_RE = /^(.*?)(\d+)$/;

function codeGroups(codes) {
    const groups = {};
    codes.forEach((raw) => {
        const code = str(raw).toUpperCase();
        const m = CODE_PARTS_RE.exec(code);
        if (!m) return;
        const g = groups[m[1]] || (groups[m[1]] = { prefix: m[1], count: 0, max: -1, width: 0, last: '' });
        const n = parseInt(m[2], 10);
        g.count++;
        if (n > g.max || (n === g.max && m[2].length > g.width)) { g.max = n; g.width = m[2].length; g.last = code; }
    });
    return Object.keys(groups).map((k) => groups[k]);
}

export function suggestNextCode(codes, typed) {
    const groups = codeGroups(codes || []);
    if (!groups.length) return null;
    const t = str(typed).toUpperCase();
    const typedPrefix = t ? (CODE_PARTS_RE.exec(t) ? CODE_PARTS_RE.exec(t)[1] : t) : '';
    const byTyped = typedPrefix ? groups.filter((g) => g.prefix === typedPrefix) : [];
    const pool = byTyped.length ? byTyped : groups;
    const g = pool.slice().sort((a, b) => (b.count - a.count) || (b.max - a.max))[0];
    const taken = {};
    (codes || []).forEach((c) => { taken[str(c).toUpperCase()] = true; });
    let n = g.max + 1;
    let code = g.prefix + String(n).padStart(g.width, '0');
    while (taken[code]) { n++; code = g.prefix + String(n).padStart(g.width, '0'); }
    if (code.length > LIMITS.itemCode) return null;
    return { code, after: g.last };
}

// Orden "natural" descendente: FIX1010 antes que FIX1009.
export function sortCodesDesc(rows) {
    return rows.slice().sort((a, b) => {
        const ma = CODE_PARTS_RE.exec(str(a.itemCode).toUpperCase());
        const mb = CODE_PARTS_RE.exec(str(b.itemCode).toUpperCase());
        if (ma && mb && ma[1] === mb[1]) return parseInt(mb[2], 10) - parseInt(ma[2], 10);
        return str(b.itemCode).localeCompare(str(a.itemCode));
    });
}

export const isItemCodeFormatOk = (v) => !blank(v) && !tooLong(v, LIMITS.itemCode) && ITEM_CODE_RE.test(str(v));
export const isUpcFormatOk = (v) => UPC_RE.test(str(v));

const toNum = (v) => (str(v) !== '' && isFinite(Number(v)) ? Number(v) : null);
export const round2 = (n) => Math.round(n * 100) / 100;

// Igual que ProductPricing.getComputedCostMin: master → min = cost / 0.95;
// distribuidor → min = Level 1 del padre (si el padre está en este wizard).
export function priceFloor(draft, idCompany, parentOf) {
    const parent = parentOf ? parentOf[idCompany] : null;
    if (parent && draft.companies[parent]) {
        return toNum(draft.companies[parent].prices.form.level1Price);
    }
    if (parent) return null; // el padre no está en el wizard: lo valida el servidor
    const cost = toNum(draft.companies[idCompany].prices.form.cost);
    return cost !== null ? round2(cost / 0.95) : null;
}

export function validatePricesForm(f, floor) {
    const e = {};
    const money = (k, required) => {
        const v = str(f[k]);
        if (v === '') { if (required) e[k] = 'Required'; return; }
        if (!MONEY_RE.test(v) || Number(v) <= 0 || Number(v) > 99999) e[k] = 'Greater than 0, max 2 decimals';
        else if (k !== 'cost' && floor !== null && Number(v) < floor) e[k] = `Cannot be below the minimum ($${floor.toFixed(2)})`;
    };
    money('cost', true);
    money('level1Price', true);
    money('level2Price', false);
    money('level3Price', false);
    money('level4Price', false);
    return e;
}

export function levelOrderWarnings(f) {
    const pairs = [['Level 2', 'level2Price', 'Level 1', 'level1Price'], ['Level 3', 'level3Price', 'Level 2', 'level2Price'], ['Level 4', 'level4Price', 'Level 3', 'level3Price']];
    const out = [];
    pairs.forEach(([label, key, prevLabel, prevKey]) => {
        const v = toNum(f[key]);
        const p = toNum(f[prevKey]);
        if (v !== null && p !== null && v > p) out.push(`${label} ($${v.toFixed(2)}) is more expensive than ${prevLabel} ($${p.toFixed(2)})`);
    });
    return out;
}

// ─── Borrador ────────────────────────────────────────────────────────────────
export const emptyTask = () => ({ status: 'idle', sent: false, attempt: 0, message: '', alreadyInSage: false, doneAt: null });
export const emptyPortalTask = () => ({ status: 'idle', message: '', doneAt: null });

const emptyCompany = () => ({
    productLine: { mode: 'existing', selected: null, form: { code: '', desc: '' }, task: emptyTask() },
    vendor: { mode: 'existing', selected: null, form: { code: '', name: '', address: '', city: '', state: '', zip: '', phone: '', email: '' }, task: emptyTask() },
    item: { task: emptyTask() },
    prices: { form: { cost: '', level1Price: '', level2Price: '', level3Price: '', level4Price: '' }, costTask: emptyTask(), portalTask: emptyPortalTask(), skipped: false },
});

export function newDraft(mainCompany) {
    return {
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        mainCompany,
        additional: [],
        stepId: 'companies',
        companies: { [mainCompany]: emptyCompany() },
        product: {
            form: { itemCode: '', description: '', fullName: '', upc: '', upc2: '', caseUpc: '', um: DEFAULT_UNIT_OF_MEASURE, weight: '', length: '', height: '', width: '' },
            portalTask: emptyPortalTask(),
            imageTask: emptyPortalTask(),
            imageName: '',
        },
        finished: false,
    };
}

export function setCompanies(draft, mainCompany, additional) {
    const ids = [mainCompany].concat(additional.filter((id) => id !== mainCompany));
    const companies = {};
    ids.forEach((id) => { companies[id] = draft.companies[id] || emptyCompany(); });
    return { ...draft, mainCompany, additional: ids.slice(1), companies };
}

export const companyOrder = (draft) => [draft.mainCompany].concat(draft.additional);

const DRAFT_PREFIX = 'newItemWizard.draft.v1.';
export const draftKey = (userName) => DRAFT_PREFIX + (userName || 'anon');

export function loadDraft(userName) {
    try {
        const raw = window.localStorage.getItem(draftKey(userName));
        if (!raw) return null;
        const d = JSON.parse(raw);
        // Un borrador viejo con Altitude como madre ya no es válido: se descarta.
        return d && d.version === 1 && !d.finished && d.mainCompany === MAIN_COMPANY_ID ? d : null;
    } catch (e) {
        return null;
    }
}

// Un task "en curso" al recargar quedó a medias: se marca como "sin confirmar"
// para que el usuario vuelva a verificar (nunca se reenvía solo).
export function normalizeResumedDraft(d) {
    const fixTask = (t) => (['checking', 'sending', 'verifying'].indexOf(t.status) >= 0
        ? { ...t, status: t.sent ? 'unconfirmed' : 'idle', message: t.sent ? 'The page was closed while waiting for Sage. Check again before resending.' : '' }
        : t);
    const fixPortal = (t) => (t.status === 'saving' ? { ...t, status: 'idle', message: '' } : t);
    const companies = {};
    Object.keys(d.companies).forEach((id) => {
        const c = d.companies[id];
        companies[id] = {
            ...c,
            productLine: { ...c.productLine, task: fixTask(c.productLine.task) },
            vendor: { ...c.vendor, task: fixTask(c.vendor.task) },
            item: { task: fixTask(c.item.task) },
            prices: { ...c.prices, costTask: fixTask(c.prices.costTask), portalTask: fixPortal(c.prices.portalTask) },
        };
    });
    return { ...d, companies, product: { ...d.product, portalTask: fixPortal(d.product.portalTask), imageTask: fixPortal(d.product.imageTask) } };
}

export function saveDraft(userName, draft) {
    try {
        window.localStorage.setItem(draftKey(userName), JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }));
    } catch (e) { /* storage bloqueado: el wizard sigue en memoria */ }
}

export function clearDraft(userName) {
    try { window.localStorage.removeItem(draftKey(userName)); } catch (e) { /* ignore */ }
}

export function anythingSent(draft) {
    return Object.keys(draft.companies).some((id) => {
        const c = draft.companies[id];
        return c.productLine.task.sent || c.vendor.task.sent || c.item.task.sent || c.prices.costTask.sent;
    });
}

// ─── Valores efectivos por empresa ──────────────────────────────────────────
export function plOf(c) {
    if (c.productLine.mode === 'existing') return c.productLine.selected ? { code: c.productLine.selected.code, desc: c.productLine.selected.desc } : null;
    return c.productLine.task.status === 'done' ? { code: c.productLine.form.code, desc: c.productLine.form.desc } : null;
}
export function vendorOf(c) {
    if (c.vendor.mode === 'existing') return c.vendor.selected ? { code: c.vendor.selected.code, name: c.vendor.selected.name } : null;
    return c.vendor.task.status === 'done' ? { code: c.vendor.form.code, name: c.vendor.form.name } : null;
}

// ─── Pasos ───────────────────────────────────────────────────────────────────
export function buildSteps(draft) {
    const steps = [{ id: 'companies', type: 'companies', label: 'Companies' }];
    companyOrder(draft).forEach((id, i) => {
        const company = COMPANIES[id].name;
        const role = i === 0 ? 'Main' : 'Additional';
        steps.push({ id: `pl-${id}`, type: 'productLine', company: id, label: 'Product Line', group: company, role });
        steps.push({ id: `vendor-${id}`, type: 'vendor', company: id, label: 'Vendor', group: company, role });
        steps.push({ id: `item-${id}`, type: 'item', company: id, label: i === 0 ? 'Product info' : 'Item', group: company, role });
        steps.push({ id: `prices-${id}`, type: 'prices', company: id, label: 'Prices & Cost', group: company, role });
    });
    steps.push({ id: 'summary', type: 'summary', label: 'Summary' });
    return steps;
}

export function itemComplete(draft, id) {
    const c = draft.companies[id];
    if (c.item.task.status !== 'done') return false;
    if (Number(id) !== Number(draft.mainCompany)) return true;
    const p = draft.product;
    return p.portalTask.status === 'done' && (!p.imageName || p.imageTask.status === 'done');
}

export function isStepComplete(draft, step) {
    if (step.type === 'companies') return !!draft.mainCompany;
    if (step.type === 'summary') return false;
    const c = draft.companies[step.company];
    if (step.type === 'productLine') return !!plOf(c);
    if (step.type === 'vendor') return !!vendorOf(c);
    if (step.type === 'item') return itemComplete(draft, step.company);
    if (step.type === 'prices') return c.prices.skipped || (c.prices.costTask.status === 'done' && c.prices.portalTask.status === 'done');
    return false;
}

// Índice máximo navegable: hasta el primer paso incompleto (incluido).
export function maxReachableIndex(draft, steps) {
    for (let i = 0; i < steps.length; i++) {
        if (!isStepComplete(draft, steps[i])) return i;
    }
    return steps.length - 1;
}

// Bloqueos: lo que ya se mandó a Sage no se edita.
export function isLocked(draft, step) {
    if (step.type === 'companies') return anythingSent(draft);
    const c = draft.companies[step.company];
    if (!c) return false;
    const itemSent = c.item.task.sent || c.item.task.status === 'done';
    if (step.type === 'productLine') return itemSent || c.productLine.task.sent;
    if (step.type === 'vendor') return itemSent || c.vendor.task.sent;
    if (step.type === 'item') {
        const main = draft.companies[draft.mainCompany];
        return main.item.task.sent || main.item.task.status === 'done';
    }
    if (step.type === 'prices') return c.prices.costTask.status === 'done' && c.prices.portalTask.status === 'done';
    return false;
}

// ─── Matchers: ¿lo que Sage tiene es lo que mandamos? ───────────────────────
// 'match' = ya está; 'conflict' = la clave existe con otros datos;
// 'differs' = existe pero hay que (re)enviar (p. ej. costo distinto).
export const matchProductLine = (form) => (rec) => (same(pick(rec, ['ProductLineDesc']), form.desc) ? 'match' : 'conflict');
export const matchVendor = (form) => (rec) => (same(pick(rec, ['VendorName']), form.name) ? 'match' : 'conflict');
export const matchItem = (plCode, vendorCode) => (rec) => {
    const pl = pick(rec, ['ProductLine', 'ProductLineNo']);
    const vendor = pick(rec, ['PrimaryVendorNo', 'VendorNo']);
    return same(pl, plCode) && same(vendor, vendorCode) ? 'match' : 'conflict';
};
export const matchCost = (cost) => (rec) => {
    const current = Number(pick(rec, ['StandardUnitCost', 'UnitCost']));
    return isFinite(current) && Math.abs(current - Number(cost)) < 0.005 ? 'match' : 'differs';
};

// ─── Runner idempotente ─────────────────────────────────────────────────────
// 1) Pregunta a Sage primero: si ya está (p. ej. un reintento tras un corte),
//    se da por hecho y NO se reenvía.
// 2) Envía. Un 4xx es rechazo claro; timeout/red/5xx son ambiguos → verificar.
// 3) Verifica con reintentos. Si no aparece: "sin confirmar" (no se reenvía
//    solo; el botón Retry vuelve a empezar por el paso 1).
// Las claves (ProductLine, VendorNo, ItemCode) quedan fijas en el borrador,
// así que un reenvío solo puede actualizar el mismo registro, nunca duplicar.
export const VERIFY_DELAYS_MS = [3000, 5000, 8000];

export async function runSageTask({ task, entity, idCompany, key, match, payload, onUpdate, wait, isCancelled, conflictMessage }) {
    let current = { ...task, message: '', attempt: 0 };
    const update = (patch) => {
        current = { ...current, ...patch };
        onUpdate(current);
        return current;
    };

    update({ status: 'checking' });
    const pre = await lookupInSage(entity, idCompany, key);
    if (isCancelled()) return current;
    if (!pre.ok) return update({ status: 'failed', message: `Could not reach Sage to check ${key} (${pre.message || 'no response'}). Nothing was sent — try again.` });
    if (pre.exists) {
        const m = match(pre.record);
        if (m === 'match') return update({ status: 'done', alreadyInSage: !current.sent, doneAt: new Date().toISOString() });
        if (m === 'conflict') return update({ status: 'conflict', message: conflictMessage || `${key} already exists in Sage with different data.` });
    }

    update({ status: 'sending' });
    const sent = await sendToSage(payload);
    if (isCancelled()) return current;
    if (sent.rejected) return update({ status: 'failed', message: `Sage rejected the request${sent.message ? `: ${sent.message}` : ''}. Nothing was changed — you can try again.` });
    update({ sent: true });

    let lastSeen = '';
    for (let i = 0; i < VERIFY_DELAYS_MS.length; i++) {
        update({ status: 'verifying', attempt: i + 1 });
        await wait(VERIFY_DELAYS_MS[i]);
        if (isCancelled()) return current;
        const res = await lookupInSage(entity, idCompany, key);
        if (isCancelled()) return current;
        if (res.ok && res.exists && match(res.record) === 'match') {
            return update({ status: 'done', alreadyInSage: false, doneAt: new Date().toISOString() });
        }
        lastSeen = !res.ok ? 'no response from Sage' : res.exists ? 'found with different data' : 'not found yet';
    }
    return update({
        status: 'unconfirmed',
        message: `Sage hasn't confirmed ${key} yet (${lastSeen}). It may still be processing — Retry checks Sage first and only resends if it's missing.`,
    });
}

export const companyName = (id) => (COMPANIES[id] ? COMPANIES[id].name : `Company ${id}`);
